import type { UseTranslationResponse } from 'react-i18next';
import { useTranslation } from 'react-i18next';

const namespace = 'entities';
export const keyPrefix = 'event_list_filters';

export const useEventListFiltersTranslation = (): UseTranslationResponse<typeof namespace, typeof keyPrefix> => {
  return useTranslation(namespace, { keyPrefix });
};

export const ruRu = (__LOCALE_RU__ ? ({
  [keyPrefix]: {
    group_general: 'Общие',
    group_comparing_attributes: 'Параметры сравнения',
    group_face_attributes: 'Атрибуты и свойства лица',
    group_body_attributes: 'Атрибуты и свойства тела',
    group_location: 'Локация',
    group_others: 'Другие',
    reset: 'Очистить',

    create_time: 'Дата события',
    create_time__gte: 'От',
    create_time__lt: 'До',
    sources: 'Источник',
    no_source: 'Без источника',
    handler_ids: 'Сценарий',
    lists: 'Список',
    tags: 'Теги',
    event_ids: 'ID событий',
    external_ids: 'Внешний ID событий',
    card_ids: 'ID карточек',
    top_similar_card_ids: 'ID карточек с макс. результатом сравнения',

    comparing_attribute: 'Параметр сравнения',
    top_similar_object_similarity: 'Схожесть',
    top_similar_object_ids: 'ID объектов с максимальным результатом сравнения',

    gender: 'Пол',
    age: 'Возраст',
    emotions: 'Эмоция',
    masks: 'Маска',
    liveness: 'Liveness',
    deepfake: 'Deepfake',
    face_ids: 'ID лиц событий',

    apparent_gender: 'Пол, определенный по телу',
    apparent_age: 'Возраст, определенный по телу',
    apparent_age_popover_title: 'Возраст, лет',
    upper_clothing: 'Верх',
    upper_clothing_colors: 'Цвет',
    sleeve_lengths: 'Рукав',
    lower_garment_colors: 'Низ',
    lower_garment_types: 'Тип',
    shoes_apparent_colors: 'Обувь',
    shoes_apparent_colors_popover: 'Цвет',
    headwear: 'Головной убор',
    headwear_color: 'Цвет',
    headwear_states: 'Головной убор',
    backpack_states: 'Рюкзак',

    location: 'Локация',
    cities: 'Город',
    areas: 'Область',
    districts: 'Район',
    streets: 'Улица',
    house_numbers: 'Дом',
    origin_longitude: 'Долгота (-180°..180°)',
    longitude_delta: 'Погрешность (0..90°)',
    origin_latitude: 'Широта (-90°.. 90°)',
    latitude_delta: 'Погрешность (0..90°)',
    location_tab_map: 'Карта',
    location_tab_address: 'Адрес',
    location_tab_coordinates: 'Координаты',
    location_map_hint: 'Выберите зону на карте',
    map_error_unsupported: 'Геолокация недоступна',
    map_error_unavailable: 'Не удалось определить местоположение',

    validation_number: 'Введите числовое значение',
    validation_range: 'Введите значение от {{left}} до {{right}}',

    track_ids: 'Идентификаторы треков',
    meta: 'Фильтр по meta',
    meta_key: 'Ключ',
    meta_value: 'Значение',
    meta_type: 'Тип данных',
    meta_operator: 'Оператор',
    meta_add: 'Добавить фильтр',
    meta_delete: 'Удалить фильтр',
  },
}) : {}) as Record<string, unknown>;

export const en = (__LOCALE_EN__ ? ({
  [keyPrefix]: {
    group_general: 'General',
    group_comparing_attributes: 'Comparison parameters',
    group_face_attributes: 'Face attributes and properties',
    group_body_attributes: 'Body attributes and properties',
    group_location: 'Location',
    group_others: 'Others',
    reset: 'Clear',

    create_time: 'Event date',
    create_time__gte: 'From',
    create_time__lt: 'To',
    sources: 'Source',
    no_source: 'No source',
    handler_ids: 'Scenario',
    lists: 'List',
    tags: 'Tags',
    event_ids: 'Event IDs',
    external_ids: 'External event IDs',
    card_ids: 'Card IDs',
    top_similar_card_ids: 'Card IDs with max match result',

    comparing_attribute: 'Comparison parameter',
    top_similar_object_similarity: 'Similarity',
    top_similar_object_ids: 'IDs of objects with maximum match result',

    gender: 'Gender',
    age: 'Age',
    emotions: 'Emotion',
    masks: 'Mask',
    liveness: 'Liveness',
    deepfake: 'Deepfake',
    face_ids: 'Face IDs from events',

    apparent_gender: 'Gender by body',
    apparent_age: 'Age by body',
    apparent_age_popover_title: 'Age, years',
    upper_clothing: 'Top',
    upper_clothing_colors: 'Color',
    sleeve_lengths: 'Sleeve',
    lower_garment_colors: 'Bottom',
    lower_garment_types: 'Type',
    shoes_apparent_colors: 'Shoes',
    shoes_apparent_colors_popover: 'Color',
    headwear: 'Headwear',
    headwear_color: 'Color',
    headwear_states: 'Headwear',
    backpack_states: 'Backpack',

    location: 'Location',
    cities: 'City',
    areas: 'Region',
    districts: 'District',
    streets: 'Street',
    house_numbers: 'House',
    origin_longitude: 'Longitude (-180°..180°)',
    longitude_delta: 'Tolerance (0..90°)',
    origin_latitude: 'Latitude (-90°..90°)',
    latitude_delta: 'Tolerance (0..90°)',
    location_tab_map: 'Map',
    location_tab_address: 'Address',
    location_tab_coordinates: 'Coordinates',
    location_map_hint: 'Select an area on the map',
    map_error_unsupported: 'Geolocation is not available',
    map_error_unavailable: 'Failed to determine location',

    validation_number: 'Enter a numeric value',
    validation_range: 'Enter a value from {{left}} to {{right}}',

    track_ids: 'Track IDs',
    meta: 'Meta filter',
    meta_key: 'Key',
    meta_value: 'Value',
    meta_type: 'Data type',
    meta_operator: 'Operator',
    meta_add: 'Add filter',
    meta_delete: 'Delete filter',
  },
}) : {}) as Record<string, unknown>;

export const esEs = (__LOCALE_ESES__ ? ({
  [keyPrefix]: {
    group_general: 'General',
    group_comparing_attributes: 'Parámetros de comparación',
    group_face_attributes: 'Atributos y propiedades del rostro',
    group_body_attributes: 'Atributos y propiedades del cuerpo',
    group_location: 'Ubicación',
    group_others: 'Otros',
    reset: 'Limpiar',

    create_time: 'Fecha del evento',
    create_time__gte: 'Desde',
    create_time__lt: 'Hasta',
    sources: 'Fuente',
    no_source: 'Sin fuente',
    handler_ids: 'Escenario',
    lists: 'Lista',
    tags: 'Etiquetas',
    event_ids: 'ID de eventos',
    external_ids: 'ID externo de eventos',
    card_ids: 'ID de tarjetas',
    top_similar_card_ids: 'ID de tarjetas con máx. resultado de comparación',

    comparing_attribute: 'Parámetro de comparación',
    top_similar_object_similarity: 'Similitud',
    top_similar_object_ids: 'ID de objetos con el máximo resultado de comparación',

    gender: 'Género',
    age: 'Edad',
    emotions: 'Emoción',
    masks: 'Mascarilla',
    liveness: 'Liveness',
    deepfake: 'Deepfake',
    face_ids: 'ID de rostros de eventos',

    apparent_gender: 'Género según el cuerpo',
    apparent_age: 'Edad según el cuerpo',
    apparent_age_popover_title: 'Edad, años',
    upper_clothing: 'Parte superior',
    upper_clothing_colors: 'Color',
    sleeve_lengths: 'Manga',
    lower_garment_colors: 'Parte inferior',
    lower_garment_types: 'Tipo',
    shoes_apparent_colors: 'Calzado',
    shoes_apparent_colors_popover: 'Color',
    headwear: 'Sombrero',
    headwear_color: 'Color',
    headwear_states: 'Sombrero',
    backpack_states: 'Mochila',

    location: 'Ubicación',
    cities: 'Ciudad',
    areas: 'Región',
    districts: 'Distrito',
    streets: 'Calle',
    house_numbers: 'Casa',
    origin_longitude: 'Longitud (-180°..180°)',
    longitude_delta: 'Margen (0..90°)',
    origin_latitude: 'Latitud (-90°..90°)',
    latitude_delta: 'Margen (0..90°)',
    location_tab_map: 'Mapa',
    location_tab_address: 'Dirección',
    location_tab_coordinates: 'Coordenadas',
    location_map_hint: 'Seleccione una zona en el mapa',
    map_error_unsupported: 'La geolocalización no está disponible',
    map_error_unavailable: 'No se pudo determinar la ubicación',

    validation_number: 'Ingrese un valor numérico',
    validation_range: 'Ingrese un valor de {{left}} a {{right}}',

    track_ids: 'ID de pistas',
    meta: 'Filtro por meta',
    meta_key: 'Clave',
    meta_value: 'Valor',
    meta_type: 'Tipo de datos',
    meta_operator: 'Operador',
    meta_add: 'Agregar filtro',
    meta_delete: 'Eliminar filtro',
  },
}) : {}) as Record<string, unknown>;

export const ptBr = (__LOCALE_PTBR__ ? ({
  [keyPrefix]: {
    group_general: 'Geral',
    group_comparing_attributes: 'Parâmetros de comparação',
    group_face_attributes: 'Atributos e propriedades do rosto',
    group_body_attributes: 'Atributos e propriedades do corpo',
    group_location: 'Localização',
    group_others: 'Outros',
    reset: 'Limpar',

    create_time: 'Data do evento',
    create_time__gte: 'De',
    create_time__lt: 'Até',
    sources: 'Fonte',
    no_source: 'Sem fonte',
    handler_ids: 'Cenário',
    lists: 'Lista',
    tags: 'Tags',
    event_ids: 'IDs de eventos',
    external_ids: 'ID externo de eventos',
    card_ids: 'IDs de cartões',
    top_similar_card_ids: 'IDs de cartões com máx. resultado de comparação',

    comparing_attribute: 'Parâmetro de comparação',
    top_similar_object_similarity: 'Similaridade',
    top_similar_object_ids: 'IDs de objetos com resultado máximo de comparação',

    gender: 'Gênero',
    age: 'Idade',
    emotions: 'Emoção',
    masks: 'Máscara',
    liveness: 'Liveness',
    deepfake: 'Deepfake',
    face_ids: 'IDs de rostos dos eventos',

    apparent_gender: 'Gênero pelo corpo',
    apparent_age: 'Idade pelo corpo',
    apparent_age_popover_title: 'Idade, anos',
    upper_clothing: 'Parte superior',
    upper_clothing_colors: 'Cor',
    sleeve_lengths: 'Manga',
    lower_garment_colors: 'Parte inferior',
    lower_garment_types: 'Tipo',
    shoes_apparent_colors: 'Calçado',
    shoes_apparent_colors_popover: 'Cor',
    headwear: 'Chapéu',
    headwear_color: 'Cor',
    headwear_states: 'Chapéu',
    backpack_states: 'Mochila',

    location: 'Localização',
    cities: 'Cidade',
    areas: 'Região',
    districts: 'Distrito',
    streets: 'Rua',
    house_numbers: 'Casa',
    origin_longitude: 'Longitude (-180°..180°)',
    longitude_delta: 'Tolerância (0..90°)',
    origin_latitude: 'Latitude (-90°..90°)',
    latitude_delta: 'Tolerância (0..90°)',
    location_tab_map: 'Mapa',
    location_tab_address: 'Endereço',
    location_tab_coordinates: 'Coordenadas',
    location_map_hint: 'Selecione uma área no mapa',
    map_error_unsupported: 'A geolocalização não está disponível',
    map_error_unavailable: 'Não foi possível determinar a localização',

    validation_number: 'Insira um valor numérico',
    validation_range: 'Insira um valor de {{left}} a {{right}}',

    track_ids: 'IDs de faixas',
    meta: 'Filtro por meta',
    meta_key: 'Chave',
    meta_value: 'Valor',
    meta_type: 'Tipo de dados',
    meta_operator: 'Operador',
    meta_add: 'Adicionar filtro',
    meta_delete: 'Excluir filtro',
  },
}) : {}) as Record<string, unknown>;
